import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
import { CaseSlideShell } from './CaseSlideShell';
import { PdfImage } from '../PdfImage';
import { boehringerImages } from '../../data/boehringerSnak';
import { nordicCampaign, nordicInsights } from '../../data/nordicCase';
const FLOW = ['Reach', 'Engagement', 'Response', 'Insight', 'Next action'] as const;
const INSIGHT_IMAGES = [{
  crop: boehringerImages.quizResults,
  alt: 'Quiz answer breakdown: 53% correct, 47% incorrect'
}, {
  crop: boehringerImages.ctaScreen,
  alt: 'Snak resource menu: Want to know more?'
}] as const;
export function CaseInsight() {
  return <CaseSlideShell step="05" eyebrow="Engagement became insight" headline={<>
          Every interaction{' '}
          <span className="text-[var(--brand)]" data-id="element-235" data-mp="1ipli9xjhxawa">creates a signal.</span>
        </>} data-id="element-234" data-mp="19rj8ashsb7wv">
      <ol className="flex flex-shrink-0 flex-wrap items-center gap-[clamp(0.25rem,0.6vw,0.6rem)]" data-id="element-236" data-mp="1epkpt8zkaz68">
        {FLOW.map((step, index) => <React.Fragment key={step} data-id="element-237" data-mp="2b2edn0u6385o" data-mpi={index}>
            <motion.li initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15 + index * 0.08,
          duration: 0.35
        }} className={`rounded-full px-[clamp(0.8rem,1.4vw,1.3rem)] py-[clamp(0.3rem,0.6vw,0.5rem)] text-[clamp(0.72rem,1.05vw,1rem)] font-bold ${step === 'Insight' ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--fg)]'}`} data-id="element-238" data-mp="2f1wyxxpwhl91">
              {step}
            </motion.li>
            {index < FLOW.length - 1 && <li aria-hidden="true" data-id="element-239" data-mp="37qkoi5homwse">
                <ChevronRightIcon className="h-4 w-4 text-[var(--brand)]/60" data-id="element-240" data-mp="37pj3b86yctwy" />
              </li>}
          </React.Fragment>)}
      </ol>

      <div className="mt-[clamp(0.9rem,1.8vw,1.6rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.6rem,1.2vw,1.1rem)] md:grid-cols-2" data-id="element-241" data-mp="3hos2cfgocyq6">
        {nordicInsights.map((insight, index) => <motion.article key={insight.figure} initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5 + index * 0.12,
        duration: 0.5
      }} className="flex min-h-0 gap-[clamp(0.8rem,1.5vw,1.4rem)] rounded-2xl bg-[var(--surface)] p-[clamp(1rem,2vw,1.9rem)]" data-id="element-242" data-mp="2n1tavtywdzec" data-mpi={index}>
            <div className="flex min-w-0 flex-1 flex-col justify-center" data-id="element-243" data-mp="3apsxchp6f3n4">
              <p className="text-[clamp(2.4rem,5.2vw,5rem)] font-extrabold leading-[0.92] tracking-[-0.05em] text-[var(--brand)]" data-id="element-244" data-mp="35t2mc8s8ay8k">
                {insight.figure}
              </p>
              <p className="mt-[clamp(0.5rem,0.9vw,0.8rem)] text-[clamp(0.95rem,1.45vw,1.4rem)] font-bold leading-snug tracking-[-0.015em] text-[var(--fg)]" data-id="element-245" data-mp="3fpxhqit7giim">
                {insight.statement}
              </p>
              <p className="mt-[clamp(0.4rem,0.7vw,0.6rem)] text-[clamp(0.7rem,0.95vw,0.9rem)] leading-snug text-[var(--muted)]" data-id="element-246" data-mp="1ld7nu12u79b8">
                {insight.proof}
              </p>
            </div>
            <div className="flex w-[38%] flex-shrink-0 items-center justify-center" data-id="element-247" data-mp="5trkd1k0irpf">
              <PdfImage crop={INSIGHT_IMAGES[index].crop} alt={INSIGHT_IMAGES[index].alt} className="max-h-full w-full rounded-xl bg-white object-contain shadow-sm ring-1 ring-black/10" data-id="element-248" data-mp="3nahu49nc1kxq" />
            </div>
          </motion.article>)}
      </div>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8,
      duration: 0.5
    }} className="mt-[clamp(0.7rem,1.3vw,1.1rem)] flex-shrink-0 text-[clamp(0.8rem,1.15vw,1.1rem)] font-semibold text-[var(--fg)]" data-id="element-249" data-mp="3ib563lkq67tk">
        Interactive communication generated engagement <em data-id="element-250" data-mp="12l24vj3ri4a7">and</em> insight from an audience BI otherwise rarely hears from.
        <span className="ml-2 text-[clamp(0.6rem,0.8vw,0.75rem)] font-normal text-[var(--muted)]" data-id="element-251" data-mp="17j3r2cwhijpw">
          Reported across all three events, {nordicCampaign.period}.
        </span>
      </motion.p>
    </CaseSlideShell>;
}